/**
 * BlockPreviewModal — 블럭 카드/그래프 노드 클릭 시 띄우는 상세 프리뷰 모달.
 *
 * 섹션 구성:
 * 1) 헤더 — 아이콘 + displayName + id + category 뱃지 + kind name
 * 2) 기본 정보 — description
 * 3) Kinds — 이 블럭이 선언한 kind 상세 (1 블럭 = 1 kind 원칙상 보통 1개)
 * 4) Domains — 이 블럭이 선언한 domain
 * 5) Relations — 이 블럭이 선언한 relation 전부 (name, displayName, description)
 * 6) Outgoing shapes — sourceKind 가 이 블럭 kind 인 shape (다른 블럭으로 나가는 연결)
 * 7) Incoming shapes — targetKind 가 이 블럭 kind 인 shape (다른 블럭에서 들어오는 연결)
 * 8) Used in packs — 이 블럭을 참조하는 팩 리스트
 *
 * 입력 (props):
 * - `block` : 대상 블럭 (필수)
 * - `allBlocks` : 카탈로그 전체 (kind → 블럭 역참조, incoming shape 계산용)
 * - `allShapes` : 마스터 shape 풀 (incoming/outgoing 계산 원천)
 * - `allPacks` : 팩 카탈로그 (used-in 역참조)
 *
 * 상호작용:
 * - ESC 키 또는 바깥 클릭 → 닫기
 * - 하단 "닫기" 버튼 → 닫기
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type {
	ExampleEdge,
	OntologyBlock,
	OntologyPack,
	RelationDef,
	ShapeDef
} from "../../api/admin-client.ts"
import { KnowledgeGraph, type KnowledgeGraphHandle } from "../graph/KnowledgeGraph.tsx"
import type { GraphEdge, GraphNode } from "../graph/types.ts"
import { packsUsingBlock } from "./pack-block-utils.ts"

interface BlockPreviewModalProps {
	readonly block: OntologyBlock
	readonly allBlocks: ReadonlyArray<OntologyBlock>
	readonly allShapes: ReadonlyArray<ShapeDef>
	readonly allPacks: ReadonlyArray<OntologyPack>
	readonly onClose: () => void
}

/**
 * 이 블럭의 kind 가 sourceKind 인 shape 목록 — "나가는" 연결.
 *
 * 1 블럭 = 1 kind 원칙상 보통 블럭 kind 1개만 비교하면 된다.
 */
export function getOutgoingShapes(
	block: OntologyBlock,
	allShapes: ReadonlyArray<ShapeDef>
): ReadonlyArray<ShapeDef> {
	const myKinds = new Set(block.slice.kinds.map((k) => k.name))
	return allShapes.filter((s) => myKinds.has(s.sourceKind))
}

/**
 * 이 블럭의 kind 가 targetKind 인 shape 목록 — "들어오는" 연결.
 *
 * 개별 블럭 정의에는 자기 블럭이 source 인 shape 만 담기므로, 들어오는 shape 를 보려면
 * 마스터 shape 풀을 순회해야 한다.
 */
export function getIncomingShapes(
	block: OntologyBlock,
	allShapes: ReadonlyArray<ShapeDef>
): ReadonlyArray<ShapeDef> {
	const myKinds = new Set(block.slice.kinds.map((k) => k.name))
	return allShapes.filter((s) => myKinds.has(s.targetKind))
}

export function BlockPreviewModal(props: BlockPreviewModalProps) {
	const { allBlocks, allShapes, allPacks, onClose } = props

	// Drawer 내부 네비게이션 스택 — outgoing/incoming shape 엔드포인트를 눌러
	// 연결된 블럭으로 이동할 때 이전 블럭을 쌓고, Back 버튼으로 복귀한다.
	// 외부에서 `props.block` 이 바뀌면 스택을 리셋한다 (새 entry 로 간주).
	const [stack, setStack] = useState<ReadonlyArray<OntologyBlock>>([props.block])
	const block = stack[stack.length - 1] ?? props.block
	const previousBlock = stack.length > 1 ? stack[stack.length - 2] : undefined
	const bodyRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		setStack([props.block])
	}, [props.block])

	const pushBlock = useCallback(
		(next: OntologyBlock) => {
			if (next.id === block.id) return
			setStack((prev) => [...prev, next])
			bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" })
		},
		[block.id]
	)

	const popBlock = useCallback(() => {
		setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))
		bodyRef.current?.scrollTo({ top: 0 })
	}, [])

	// kind 이름 → 블럭 역참조 — 상대 블럭의 아이콘/표시명을 shape 리스트에 붙일 때 사용.
	const kindToBlock = useMemo(() => {
		const map = new Map<string, OntologyBlock>()
		for (const b of allBlocks) {
			const kind = b.slice.kinds[0]
			if (kind) map.set(kind.name, b)
		}
		return map
	}, [allBlocks])

	const outgoing = useMemo(() => getOutgoingShapes(block, allShapes), [block, allShapes])
	const incoming = useMemo(() => getIncomingShapes(block, allShapes), [block, allShapes])
	const usingPacks = useMemo(() => packsUsingBlock(allPacks, block.id), [allPacks, block.id])

	/** 관계 그래프 데이터 — 중앙 self + outgoing target 블럭들 + incoming source 블럭들 합집합.
	 * Edge 는 shape 의 (sourceKind, relation, targetKind) 를 그대로 유지해 KnowledgeGraph 가 화살표로 렌더. */
	const relationsGraph = useMemo(
		() => buildRelationsGraph(block, outgoing, incoming, kindToBlock),
		[block, outgoing, incoming, kindToBlock]
	)
	const graphRef = useRef<KnowledgeGraphHandle>(null)

	// ESC 로 닫기
	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [onClose])

	const firstKind = block.slice.kinds[0]

	return (
		<dialog
			className="drawer-backdrop"
			open
			aria-label={`${block.displayName} 블럭 상세`}
			onMouseDown={(e) => e.currentTarget === e.target && onClose()}
		>
			<aside
				className="drawer block-preview-drawer block-preview-drawer--dual-pane"
				onMouseDown={(e) => e.stopPropagation()}
				aria-modal="true"
			>
				<div className="modal-head">
					{previousBlock && (
						<button
							type="button"
							className="drawer-back"
							onClick={popBlock}
							aria-label={`${previousBlock.displayName} 로 돌아가기`}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2.2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<title>뒤로</title>
								<path d="m15 18-6-6 6-6" />
							</svg>
							<span className="drawer-back-label">
								<span aria-hidden>{previousBlock.icon}</span>
								{previousBlock.displayName}
							</span>
						</button>
					)}
					<button type="button" className="drawer-close" onClick={onClose} aria-label="닫기">
						<svg
							width="18"
							height="18"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<title>닫기</title>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
					<div className="block-preview-header">
						<span className="block-preview-icon" aria-hidden>
							{block.icon}
						</span>
						<div className="block-preview-title">
							<h2>{block.displayName}</h2>
							<div className="block-preview-meta">
								<span className={`badge blocks-page-card-cat cat-${block.category}`}>{block.category}</span>
								<IdBadge label="block id" value={block.id} />
								{firstKind && <IdBadge label="kind id" value={firstKind.name} />}
							</div>
						</div>
					</div>
					<p>{block.description}</p>
				</div>

				<div className="modal-body" ref={bodyRef}>
					<div className="block-preview-graph-side">
						{(relationsGraph.nodes.length > 1 || relationsGraph.edges.length > 0) && (
							<section className="block-preview-section block-preview-graph-section">
								<div className="block-preview-graph-head">
									<h3>관계 그래프</h3>
									<button
										type="button"
										className="btn btn-ghost btn-sm"
										onClick={() => graphRef.current?.fitView()}
										title="그래프를 캔버스에 맞춰 재정렬"
									>
										⟲ 맞춤
									</button>
								</div>
								<p className="muted block-preview-graph-hint">
									이 블럭({block.displayName}) 을 중심으로 들어오는/나가는 연결을 시각화. 노드 클릭 시 해당
									블럭으로 이동.
								</p>
								<div className="block-preview-graph-canvas">
									<KnowledgeGraph
										ref={graphRef}
										nodes={relationsGraph.nodes}
										edges={relationsGraph.edges}
										onNodeSelect={(node: GraphNode | null) => {
											if (!node) return
											const next = allBlocks.find((b) => b.id === node.id)
											if (next && next.id !== block.id) pushBlock(next)
										}}
									/>
								</div>
							</section>
						)}
					</div>
					<div className="block-preview-details-side">
						{block.example && (
							<section className="block-preview-section block-preview-example">
								<h3>예시 인스턴스</h3>
								{block.example.summary && (
									<p className="block-preview-example-summary">{block.example.summary}</p>
								)}
								<div className="block-preview-example-node">
									<span className="block-preview-example-badge">node</span>
									<span className="block-preview-example-kind">
										<span aria-hidden>{block.icon}</span>
										<code className="inline">{block.example.node.kind}</code>
									</span>
									<div className="block-preview-example-body">
										<strong>{block.example.node.displayName ?? block.example.node.name}</strong>
										<code className="inline block-preview-example-id">{block.example.node.name}</code>
										{block.example.node.description && (
											<span className="muted">{block.example.node.description}</span>
										)}
									</div>
								</div>
								{block.example.edges && block.example.edges.length > 0 && (
									<ul className="block-preview-example-edges">
										{block.example.edges.map((edge, idx) => (
											<li
												key={`${edge.from.kind}|${edge.from.name}|${edge.relation}|${edge.to.kind}|${edge.to.name}`}
												className="block-preview-example-edge"
											>
												<span className="block-preview-example-badge">관계 {idx + 1}</span>
												<ExampleEdgeRow edge={edge} relations={block.slice.relations} />
											</li>
										))}
									</ul>
								)}
							</section>
						)}

						<section className="block-preview-section">
							<h3>종류</h3>
							{block.slice.kinds.length === 0 ? (
								<p className="muted">선언된 kind 가 없습니다.</p>
							) : (
								<ul className="block-preview-kinds">
									{block.slice.kinds.map((k) => (
										<li key={k.name}>
											<span className="block-preview-kind-icon" aria-hidden>
												{k.icon ?? block.icon}
											</span>
											<div className="block-preview-kind-body">
												<strong>{k.displayName}</strong>
												<IdBadge label="kind id" value={k.name} />
												{k.description && <span className="muted">{k.description}</span>}
											</div>
											{k.color && (
												<span
													className="block-preview-kind-swatch"
													style={{ background: k.color }}
													title={`color: ${k.color}`}
													aria-hidden
												/>
											)}
										</li>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>도메인</h3>
							{block.slice.domains.length === 0 ? (
								<p className="muted">선언된 domain 이 없습니다.</p>
							) : (
								<div className="block-preview-chips">
									{block.slice.domains.map((d) => (
										<span key={d.name} className="chip" title={d.description ?? d.displayName}>
											{d.displayName}
											<IdBadge label="domain id" value={d.name} />
										</span>
									))}
								</div>
							)}
						</section>

						<section className="block-preview-section">
							<h3>관계 ({block.slice.relations.length})</h3>
							{block.slice.relations.length === 0 ? (
								<p className="muted">선언된 relation 이 없습니다.</p>
							) : (
								<ul className="block-preview-relations">
									{block.slice.relations.map((r) => (
										<li key={r.name}>
											<IdBadge label="relation" value={r.name} emphasis />
											<strong>{r.displayName}</strong>
											{r.description && <span className="muted">{r.description}</span>}
										</li>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>나가는 shape ({outgoing.length})</h3>
							{outgoing.length === 0 ? (
								<p className="muted">이 블럭에서 나가는 연결이 없습니다.</p>
							) : (
								<ul className="block-preview-shape-list">
									{outgoing.map((s) => (
										<ShapeRow
											key={`${s.sourceKind}|${s.relation}|${s.targetKind}`}
											shape={s}
											kindToBlock={kindToBlock}
											emphasise="target"
											onSelectBlock={pushBlock}
										/>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>들어오는 shape ({incoming.length})</h3>
							{incoming.length === 0 ? (
								<p className="muted">이 블럭으로 들어오는 연결이 없습니다.</p>
							) : (
								<ul className="block-preview-shape-list">
									{incoming.map((s) => (
										<ShapeRow
											key={`${s.sourceKind}|${s.relation}|${s.targetKind}`}
											shape={s}
											kindToBlock={kindToBlock}
											emphasise="source"
											onSelectBlock={pushBlock}
										/>
									))}
								</ul>
							)}
						</section>

						<section className="block-preview-section">
							<h3>사용 중인 팩 ({usingPacks.length})</h3>
							{usingPacks.length === 0 ? (
								<p className="muted">아직 이 블럭을 참조하는 팩이 없습니다.</p>
							) : (
								<div className="block-preview-chips">
									{usingPacks.map((p) => (
										<span key={p.id} className="chip blocks-page-card-pack" title={p.description}>
											{p.icon} {p.displayName}
										</span>
									))}
								</div>
							)}
						</section>
					</div>
				</div>

				<div className="modal-foot">
					<span className="drawer-hint muted" aria-hidden>
						ESC 로 닫기
					</span>
					<div style={{ display: "flex", gap: 8 }}>
						<ShareLinkButton blockId={block.id} />
						<button type="button" className="btn btn-primary" onClick={onClose}>
							닫기
						</button>
					</div>
				</div>
			</aside>
		</dialog>
	)
}

/**
 * 현재 블럭 상세로 바로 열리는 공유 URL 을 클립보드에 복사.
 * `/blocks?block=<id>` 형태 — 받은 사람은 해당 블럭 모달이 자동으로 열린다.
 */
function ShareLinkButton({ blockId }: { readonly blockId: string }) {
	const [copied, setCopied] = useState(false)
	const onClick = () => {
		const url = new URL(window.location.href)
		url.pathname = "/blocks"
		url.searchParams.set("block", blockId)
		url.hash = ""
		void navigator.clipboard.writeText(url.toString()).catch(() => undefined)
		setCopied(true)
		setTimeout(() => setCopied(false), 1600)
	}
	return (
		<button
			type="button"
			className="btn btn-ghost"
			onClick={onClick}
			title="이 블럭 상세로 바로 열리는 링크 복사"
		>
			{copied ? "복사됨 ✓" : "링크 복사"}
		</button>
	)
}

/**
 * 예시 엣지 1줄 — `from.name —[RELATION]→ to.name` 포맷.
 *
 * 실제 NodeId 수준의 식별자 대신 kind + 이름(identifier) 만 표시한다 — serviceSlug/domain 은 테넌트별이라
 * 블럭 단위 예시에서는 의미가 약하다. 사용자는 `(kind:name)` 두 조각만 봐도 구조를 파악할 수 있다.
 */
function ExampleEdgeRow(props: {
	readonly edge: ExampleEdge
	readonly relations?: ReadonlyArray<RelationDef>
}) {
	const { edge, relations } = props
	/** relation 이름 → displayName lookup. 없으면 raw 이름 그대로 사용. */
	const relationLabel = relations?.find((r) => r.name === edge.relation)?.displayName ?? edge.relation
	return (
		<span className="block-preview-example-triple">
			<span className="block-preview-example-endpoint">
				<code className="inline">{edge.from.kind}</code>
				<strong>{edge.from.displayName ?? edge.from.name}</strong>
			</span>
			<span className="block-preview-example-arrow" aria-hidden>
				—
			</span>
			<code className="inline block-preview-example-relation">{relationLabel}</code>
			<span className="block-preview-example-arrow" aria-hidden>
				→
			</span>
			<span className="block-preview-example-endpoint">
				<code className="inline">{edge.to.kind}</code>
				<strong>{edge.to.displayName ?? edge.to.name}</strong>
			</span>
		</span>
	)
}

/**
 * shape 1줄 — `source —[RELATION]→ target` 포맷.
 *
 * `emphasise` 가 `"source"` 면 source 블럭, `"target"` 이면 target 블럭을 색상 강조.
 * BlockPreviewModal 에서 "내 블럭" 이 아닌 반대편을 드러내기 위해 사용하며,
 * 강조된 엔드포인트는 `onSelectBlock` 으로 drawer 내부 네비게이션을 발생시킨다
 * (kindToBlock 에 해당 kind 블럭이 있을 때만 버튼으로 렌더).
 */
function ShapeRow(props: {
	readonly shape: ShapeDef
	readonly kindToBlock: ReadonlyMap<string, OntologyBlock>
	readonly emphasise: "source" | "target"
	readonly onSelectBlock: (block: OntologyBlock) => void
}) {
	const { shape, kindToBlock, emphasise, onSelectBlock } = props
	const src = kindToBlock.get(shape.sourceKind)
	const tgt = kindToBlock.get(shape.targetKind)
	return (
		<li className="block-preview-shape-row">
			<ShapeEndpoint
				block={src}
				fallbackKind={shape.sourceKind}
				isEmphasised={emphasise === "source"}
				onSelectBlock={onSelectBlock}
			/>
			<span className="block-preview-shape-arrow" aria-hidden>
				—
			</span>
			<code className="inline block-preview-shape-relation" title={shape.description}>
				{shape.relation}
			</code>
			<span className="block-preview-shape-arrow" aria-hidden>
				→
			</span>
			<ShapeEndpoint
				block={tgt}
				fallbackKind={shape.targetKind}
				isEmphasised={emphasise === "target"}
				onSelectBlock={onSelectBlock}
			/>
		</li>
	)
}

/**
 * Shape 의 한쪽 엔드포인트 — kindToBlock 에 블럭이 있고 `isEmphasised` 인 경우
 * `<button>` 으로 렌더해 클릭 시 drawer 내부 네비게이션(onSelectBlock) 을 발생시킨다.
 * "내 블럭" 쪽 (isEmphasised=false) 과 kindToBlock 에 없는 kind 는 일반 span 으로 둔다.
 */
function ShapeEndpoint(props: {
	readonly block: OntologyBlock | undefined
	readonly fallbackKind: string
	readonly isEmphasised: boolean
	readonly onSelectBlock: (block: OntologyBlock) => void
}) {
	const { block, fallbackKind, isEmphasised, onSelectBlock } = props
	const label = block?.displayName ?? fallbackKind
	const icon = block?.icon ?? "◦"
	const canJump = Boolean(block) && isEmphasised
	const className = `block-preview-shape-endpoint${isEmphasised ? " is-emphasised" : ""}${
		canJump ? " block-preview-shape-endpoint-btn" : ""
	}`
	if (canJump && block) {
		return (
			<button
				type="button"
				className={className}
				onClick={() => onSelectBlock(block)}
				title={`${block.displayName} 블럭으로 이동`}
			>
				<span aria-hidden>{icon}</span>
				<span>{label}</span>
				<span className="block-preview-shape-endpoint-arrow" aria-hidden>
					›
				</span>
			</button>
		)
	}
	return (
		<span className={className} title={block?.description}>
			<span aria-hidden>{icon}</span>
			<span>{label}</span>
		</span>
	)
}

/**
 * IdBadge — raw identifier (kind id / domain id / relation name / block id) 를 렌더할 때
 * **무슨 식별자인지** label prefix 를 명시해 맥락 없는 `persona` / `org` / `MEMBER_OF` 가
 * 단독 노출되던 문제를 해결한다 (2026-04 피드백).
 *
 * `emphasis` 가 true 이면 accent 색으로 강조 (relation 처럼 관계형 식별자 강조용).
 */
function IdBadge(props: { readonly label: string; readonly value: string; readonly emphasis?: boolean }) {
	return (
		<span className={`id-badge${props.emphasis ? " id-badge--emphasis" : ""}`}>
			<span className="id-badge__label">{props.label}</span>
			<code className="id-badge__value">{props.value}</code>
		</span>
	)
}

/**
 * 블럭 중심 미니 관계 그래프용 노드/엣지 빌더.
 *
 * - Self 노드: 카테고리 기반 색 강조 (`_color`) + 약간 큰 size
 * - Outgoing target 노드 + Incoming source 노드: kindToBlock 으로 실제 블럭 메타 매핑 후 노드화.
 *   동일 블럭이 outgoing/incoming 양쪽에 있으면 dedupe.
 * - Edge `source`/`target` 은 블럭 id 를 쓴다 (KnowledgeGraph 의 `nodeId="id"` 규약).
 */
function buildRelationsGraph(
	self: OntologyBlock,
	outgoing: ReadonlyArray<ShapeDef>,
	incoming: ReadonlyArray<ShapeDef>,
	kindToBlock: ReadonlyMap<string, OntologyBlock>
): { readonly nodes: ReadonlyArray<GraphNode>; readonly edges: ReadonlyArray<GraphEdge> } {
	const selfNode: GraphNode = {
		id: self.id,
		domain: self.category,
		kind: self.slice.kinds[0]?.name ?? self.id,
		name: self.displayName,
		category: self.category,
		_size: 4
	}
	const nodeMap = new Map<string, GraphNode>([[self.id, selfNode]])
	const edges: GraphEdge[] = []

	const addBlockNode = (b: OntologyBlock) => {
		if (nodeMap.has(b.id)) return
		nodeMap.set(b.id, {
			id: b.id,
			domain: b.category,
			kind: b.slice.kinds[0]?.name ?? b.id,
			name: b.displayName,
			category: b.category
		})
	}

	for (const s of outgoing) {
		const tgt = kindToBlock.get(s.targetKind)
		if (!tgt) continue
		addBlockNode(tgt)
		edges.push({
			id: `out:${self.id}|${s.relation}|${tgt.id}`,
			source: self.id,
			target: tgt.id,
			relation: s.relation
		})
	}
	for (const s of incoming) {
		const src = kindToBlock.get(s.sourceKind)
		if (!src) continue
		// self-loop 은 outgoing 에서 이미 렌더. 중복 edge 생성 안 함.
		if (src.id === self.id) continue
		addBlockNode(src)
		edges.push({
			id: `in:${src.id}|${s.relation}|${self.id}`,
			source: src.id,
			target: self.id,
			relation: s.relation
		})
	}
	return { nodes: Array.from(nodeMap.values()), edges }
}
